// Утилиты для работы с Push уведомлениями
import { API_URL } from '../config';
import { safeFetch } from './safeFetch';

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  // Проверка поддержки Push уведомлений
  isSupported() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isOpera = userAgent.includes('opr/') || userAgent.includes('opera');
    const isTelegramBrowser = userAgent.includes('telegram');
    
    // Отключаем Push для проблемных браузеров
    if (isOpera || isTelegramBrowser) {
      console.log('Push уведомления отключены для браузера:', userAgent);
      return false;
    }
    
    const isSupported = 'serviceWorker' in navigator &&
           'PushManager' in window &&
           'Notification' in window &&
           (window.isSecureContext === true);
    
    return isSupported;
  }

  // Регистрация Service Worker
  async registerServiceWorker() {
    if (!this.isSupported()) {
      throw new Error('Push уведомления не поддерживаются в этом браузере');
    }

    try {
      // Проверяем, есть ли уже зарегистрированный Service Worker
      const existingRegistration = await navigator.serviceWorker.getRegistration('/') || await navigator.serviceWorker.getRegistration();
      if (existingRegistration) {
        console.log('Service Worker уже зарегистрирован:', existingRegistration);
        this.registration = existingRegistration;
        return this.registration;
      }

      console.log('Регистрация Service Worker...');
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker зарегистрирован:', this.registration);

      // Ждем активации Service Worker
      if (this.registration.installing) {
        await new Promise((resolve) => {
          this.registration.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
              resolve();
            }
          });
        });
      }

      return this.registration;
    } catch (error) {
      console.error('Ошибка регистрации Service Worker:', error);
      throw error;
    }
  }

  // Запрос разрешения на уведомления
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Уведомления не поддерживаются');
    }

    let permission = Notification.permission;

    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch (e) {
        console.warn('Notification.requestPermission() не поддерживается:', e);
        permission = 'denied';
      }
    }

    if (permission !== 'granted') {
      throw new Error('Разрешение на уведомления не получено');
    }

    return permission;
  }

  // Подписка на Push уведомления
  async subscribeToPush() {
    try {
      console.log('=== ДИАГНОСТИКА PUSH ПОДПИСКИ ===');
      console.log('User Agent:', navigator.userAgent);
      console.log('Service Worker поддержка:', 'serviceWorker' in navigator);
      console.log('Push Manager поддержка:', 'PushManager' in window);
      console.log('Notification поддержка:', 'Notification' in window);
      console.log('Текущие разрешения:', Notification.permission);

      if (!this.registration) {
        console.log('Регистрируем Service Worker...');
        await this.registerServiceWorker();
      }

      console.log('Service Worker зарегистрирован:', !!this.registration);
      console.log('Push Manager доступен:', !!this.registration?.pushManager);

      await this.requestPermission();
      console.log('Разрешения после запроса:', Notification.permission);

      // Проверяем существующую подписку
      const existingSubscription = await this.registration.pushManager.getSubscription();
      console.log('Существующая подписка:', existingSubscription);

      if (existingSubscription) {
        console.log('Отписываемся от существующей подписки...');
        await existingSubscription.unsubscribe();
      }

      // Получаем VAPID публичный ключ динамически с бэкенда
      console.log('Запрашиваем VAPID публичный ключ у сервера...');
      const pkResponse = await safeFetch(`${API_URL}/api/push/public-key`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const VAPID_PUBLIC_KEY = pkResponse?.publicKey;
      if (!VAPID_PUBLIC_KEY) {
        throw new Error('Не удалось получить VAPID публичный ключ с сервера');
      }

      console.log('Получен VAPID публичный ключ от сервера, длина:', VAPID_PUBLIC_KEY.length);
      const vapidPublicKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

      console.log('VAPID ключ длина:', vapidPublicKey.length);
      console.log('Создаем новую подписку...');
      
      const subscribeOptions = {
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      };
      
      console.log('Опции подписки:', subscribeOptions);
      
      this.subscription = await this.registration.pushManager.subscribe(subscribeOptions);

      console.log('Push подписка создана успешно!');

      // Отправляем подписку на сервер
      await this.sendSubscriptionToServer(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('Ошибка подписки на Push уведомления:', error);
      throw error;
    }
  }

  // Отправка подписки на сервер
  async sendSubscriptionToServer(subscription) {
    try {
      // Сначала очищаем старые подписки
      await safeFetch(`${API_URL}/api/push/clear-subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const subscriptionData = subscription.toJSON();
      
      const response = await safeFetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subscription: subscriptionData,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
      return response;
    } catch (error) {
      console.error('Ошибка отправки подписки на сервер:', error);
      throw error;
    }
  }

  // Отписка от Push уведомлений
  async unsubscribe() {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();
        console.log('Отписка от Push уведомлений выполнена');
      }

      // Уведомляем сервер об отписке
      await safeFetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      this.subscription = null;
    } catch (error) {
      console.error('Ошибка отписки от Push уведомлений:', error);
      throw error;
    }
  }

  // Проверка подписки
  async isSubscribed() {
    try {
      const subscription = await this.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Ошибка проверки подписки:', error);
      return false;
    }
  }

  // Проверка текущей подписки
  async getSubscription() {
    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      this.subscription = await this.registration.pushManager.getSubscription();
      return this.subscription;
    } catch (error) {
      console.error('Ошибка получения подписки:', error);
      return null;
    }
  }

  // Тестовое уведомление
  async sendTestNotification() {
    try {
      if (!this.subscription) {
        await this.subscribeToPush();
      }

      const response = await safeFetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Тестовое уведомление отправлено:', response);
      return response;
    } catch (error) {
      console.error('Ошибка отправки тестового уведомления:', error);
      throw error;
    }
  }

  // Конвертация VAPID ключа
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Инициализация Push сервиса
  async init() {
    try {
      console.log('Инициализация Push сервиса...');
      
      // Регистрируем Service Worker
      await this.registerServiceWorker();
      
      // Проверяем существующую подписку
      const existingSubscription = await this.getSubscription();
      
      if (existingSubscription) {
        console.log('Найдена существующая подписка:', existingSubscription);
        this.subscription = existingSubscription;
      }
      
      // Показываем предложение установить приложение
      this.showInstallPrompt();
      
      return true;
    } catch (error) {
      console.error('Ошибка инициализации Push сервиса:', error);
      throw error;
    }
  }

  // Инициализация PWA
  async initializePWA() {
    return await this.init();
  }

  // Показ предложения установить приложение
  showInstallPrompt() {
    let deferredPrompt;

    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('Событие beforeinstallprompt сработало');
      e.preventDefault();
      deferredPrompt = e;
      
      // Показываем кнопку установки (можно добавить в UI)
      console.log('Приложение можно установить');
    });

    window.addEventListener('appinstalled', () => {
      console.log('Приложение установлено');
      deferredPrompt = null;
    });
  }
}

export default PushNotificationService;
